import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '@/lib/api';
import { formatInAppZone, APP_TIME_ZONE_LABEL } from '@/lib/formatters';
import { Shield, FileText } from 'lucide-react';
import '@/styles/authenticated.css';


const AgreementModal = ({ onSigned }) => {
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeAuthority, setAgreeAuthority] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [hasPreviousSignature, setHasPreviousSignature] = useState(false);

  useEffect(() => {
    fetchAgreement();
  }, []);

  // Block ESC key
  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') e.preventDefault(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const fetchAgreement = async () => {
    try {
      // Each request is tolerated independently, as before: a failing status
      // lookup must not stop the agreement itself from loading. Promise.all
      // over throwing helpers would reject on the first failure, so the
      // per-call .catch keeps the original partial-success behaviour.
      const [status, data] = await Promise.all([
        apiGet('/agreements/status').catch(() => null),
        apiGet('/agreements/active/funder_non_circumvention').catch(() => null),
      ]);
      if (status) setHasPreviousSignature(status.has_previous_signature);
      if (data) setAgreement(data);
    } catch (error) {
      console.error('Failed to load agreement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSign = useCallback(async () => {
    if (!agreeTerms || !agreeAuthority || signedName.trim().length < 2) return;
    setSubmitting(true);
    try {
      await apiPost('/agreements/sign', {
        agreement_version_id: agreement.agreement_id,
        signed_name: signedName.trim(),
        agree_terms: agreeTerms,
        agree_authority: agreeAuthority,
      });
      toast.success('Agreement signed successfully');
      if (onSigned) onSigned();
    } catch (error) {
      toast.error('Failed to sign agreement');
    } finally {
      setSubmitting(false);
    }
  }, [agreeTerms, agreeAuthority, signedName, agreement, onSigned]);

  const canSubmit = agreeTerms && agreeAuthority && signedName.trim().length >= 2;
  // The timestamp shown against the signature. Pinned to Eastern and labelled
  // so the record reads the same to the signer and to whoever reviews it later
  // from another timezone.
  const currentDate = `${formatInAppZone(new Date(), { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })} ${APP_TIME_ZONE_LABEL}`;

  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner-border" style={{ color: '#fff', width: '3rem', height: '3rem' }} role="status" />
      </div>
    );
  }

  if (!agreement) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} data-testid="agreement-modal-overlay">
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 740, width: '100%', maxHeight: '95vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }} data-testid="agreement-modal">

        {/* Header */}
        <div style={{ padding: '1.5rem 2rem', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <div style={{ width: 40, height: 40, borderRadius: 8, background: 'linear-gradient(135deg, #070689, #1e40af)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Shield size={20} color="#fff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#1e293b' }} data-testid="agreement-title">{agreement.title}</h2>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Version {agreement.version_number}</span>
            </div>
          </div>
          {hasPreviousSignature && agreement.summary_of_changes && (
            <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: '0.75rem 1rem', marginTop: '0.75rem' }} data-testid="agreement-changes-notice">
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#92400e', fontWeight: 600 }}>This agreement has been updated since you last signed.</p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#92400e' }}>{agreement.summary_of_changes}</p>
            </div>
          )}
        </div>

        {/* Agreement Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem 2rem' }} data-testid="agreement-body">
          <div dangerouslySetInnerHTML={{ __html: agreement.agreement_body }} style={{ fontSize: '0.9rem', lineHeight: 1.7, color: '#334155' }} className="agreement-content" />
        </div>

        {/* Signing Section */}
        <div style={{ padding: '1.5rem 2rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0, borderRadius: '0 0 16px 16px' }}>
          {/* Checkboxes */}
          <label style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '0.75rem' }}>
            <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} data-testid="agree-terms-checkbox" style={{ marginTop: 3, accentColor: '#070689', width: 18, height: 18 }} />
            <span style={{ fontSize: '0.85rem', color: '#334155', lineHeight: 1.5 }}>I agree to the terms of this agreement.</span>
          </label>

          <label style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '1rem' }}>
            <input type="checkbox" checked={agreeAuthority} onChange={(e) => setAgreeAuthority(e.target.checked)} data-testid="agree-authority-checkbox" style={{ marginTop: 3, accentColor: '#070689', width: 18, height: 18 }} />
            <span style={{ fontSize: '0.85rem', color: '#334155', lineHeight: 1.5 }}>I certify that I am authorized to enter into this agreement on behalf of my organization and agree that this agreement binds my entire firm and its employees, agents, contractors, affiliates, and representatives.</span>
          </label>

          {/* Signature + Date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', display: 'block' }}>Digital Signature (Full Legal Name)</label>
              <input type="text" value={signedName} onChange={(e) => setSignedName(e.target.value)} placeholder="Type your full legal name" data-testid="agreement-signature-input" style={{ width: '100%', padding: '0.625rem 0.875rem', border: '2px solid #cbd5e1', borderRadius: 8, fontSize: '1rem', fontFamily: "'Georgia', serif", fontStyle: 'italic', background: '#fff', outline: 'none', transition: 'border-color 0.2s' }} onFocus={(e) => e.target.style.borderColor = '#070689'} onBlur={(e) => e.target.style.borderColor = '#cbd5e1'} />
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', display: 'block' }}>Date & Time</label>
              <div style={{ padding: '0.625rem 0.875rem', background: '#f1f5f9', borderRadius: 8, border: '2px solid #e2e8f0', fontSize: '0.9rem', color: '#475569' }} data-testid="agreement-date">{currentDate}</div>
            </div>
          </div>

          {/* Submit */}
          <button onClick={handleSign} disabled={!canSubmit || submitting} data-testid="sign-agreement-btn" style={{ width: '100%', padding: '0.875rem', borderRadius: 10, border: 'none', background: canSubmit ? 'linear-gradient(135deg, #070689, #1e40af)' : '#94a3b8', color: '#fff', fontWeight: 700, fontSize: '1rem', cursor: canSubmit ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'all 0.2s' }}>
            <FileText size={18} />
            {submitting ? 'Signing...' : 'Sign Agreement'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgreementModal;
